import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { BulkSettleResult, TitleFilter, TitleListResult, TitleView } from "@cx/contracts";
import { MAX_BULK_SETTLE, formatBRL, formatDateBR, todayInSaoPaulo, type TitleSide } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { SIDE, bulkSettleSummary, installmentLabel, titleBadge } from "./labels";
import { TitleDialog, useTitleOptions } from "./TitleDialogs";

const FILTERS: { key: TitleFilter; label: (side: TitleSide) => string }[] = [
  { key: "open", label: () => "Em aberto" },
  { key: "overdue", label: () => "Atrasadas" },
  { key: "paid", label: (s) => (s === "R" ? "Recebidas" : "Pagas") },
  { key: "canceled", label: () => "Canceladas" },
  { key: "all", label: () => "Todas" },
];

export const ReceivablesPage = () => <TitlesListPage side="R" />;
export const PayablesPage = () => <TitlesListPage side="P" />;

/** Contas a receber / a pagar (RF-FIN-01): filtros por situação e período, totais, inclusão e baixa em lote. */
function TitlesListPage({ side }: { side: TitleSide }): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const s = SIDE[side];
  const canCreate = can(perms, s.module, "create");
  const canUpdate = can(perms, s.module, "update");
  const [status, setStatus] = useState<TitleFilter>("open");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<Map<string, TitleView>>(new Map());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [failures, setFailures] = useState<{ label: string; message: string }[]>([]);
  const term = q.trim();
  const list = useInfiniteQuery({
    queryKey: ["titles", companyId, side, "list", status, term, from, to],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<TitleListResult>(
        `${s.api}?${new URLSearchParams({
          status,
          ...(term ? { q: term } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const summary = list.data?.pages[0]?.summary;
  const settleable = (t: TitleView) => t.status === "open" || t.status === "partial";
  const picked = [...selected.values()];
  const toggle = (t: TitleView) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(t.id)) next.delete(t.id);
      else next.set(t.id, t);
      return next;
    });
  const changed = () => qc.invalidateQueries({ queryKey: ["titles", companyId, side] });

  return (
    <>
      <ContentHeader
        title={s.title}
        breadcrumb={["Finanças", s.title]}
        actions={
          canCreate && (
            <>
              <Link
                to="/preferencias/importacoes"
                search={{ tipo: s.module }}
                className="btn btn-outline-secondary"
              >
                Importar planilha
              </Link>
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                Incluir conta
              </button>
            </>
          )
        }
      />
      <ul className="nav nav-tabs mb-3" role="tablist" aria-label="Situação">
        {FILTERS.map((f) => (
          <li key={f.key} className="nav-item" role="presentation">
            <button
              type="button"
              role="tab"
              aria-selected={status === f.key}
              className={`nav-link${status === f.key ? " active" : ""}`}
              onClick={() => {
                setStatus(f.key);
                setSelected(new Map());
              }}
            >
              {f.label(side)}
            </button>
          </li>
        ))}
      </ul>
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-4">
            <label className="visually-hidden" htmlFor="titles-q">
              Pesquisar
            </label>
            <input
              id="titles-q"
              type="search"
              className="form-control"
              placeholder={`Pesquisar por ${s.contact.toLowerCase()}, histórico ou nº documento`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="col-6 col-md-2">
            <label className="visually-hidden" htmlFor="titles-from">
              Vencimento a partir de
            </label>
            <input
              id="titles-from"
              type="date"
              className="form-control"
              title="Vencimento a partir de"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="col-6 col-md-2">
            <label className="visually-hidden" htmlFor="titles-to">
              Vencimento até
            </label>
            <input
              id="titles-to"
              type="date"
              className="form-control"
              title="Vencimento até"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>
        {picked.length > 0 && (
          <div
            className="d-flex flex-wrap align-items-center gap-2 px-3 pt-3"
            role="toolbar"
            aria-label="Ações em lote"
          >
            <span className="small">
              {picked.length} {picked.length === 1 ? "selecionada" : "selecionadas"} ·{" "}
              {formatBRL(picked.reduce((a, t) => a + t.openCents, 0))}
            </span>
            {canUpdate && (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={picked.length > MAX_BULK_SETTLE}
                onClick={() => setBulkOpen(true)}
              >
                {s.bulk}
              </button>
            )}
            <button type="button" className="btn btn-sm btn-link" onClick={() => setSelected(new Map())}>
              Limpar seleção
            </button>
            {picked.length > MAX_BULK_SETTLE && (
              <span className="text-danger small">No máximo {MAX_BULK_SETTLE} contas por vez.</span>
            )}
          </div>
        )}
        {failures.length > 0 && (
          <div className="alert alert-warning m-3 mb-0" role="alert" data-testid="bulk-failures">
            <strong>Contas não baixadas:</strong>
            <ul className="mb-0">
              {failures.map((f) => (
                <li key={f.label}>
                  {f.label}: {f.message}
                </li>
              ))}
            </ul>
          </div>
        )}
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhuma conta"
            description={
              side === "R"
                ? "As contas a receber dos pedidos aparecem aqui; inclua também as avulsas e as recorrentes."
                : "Registre as contas a pagar dos fornecedores, parceladas ou recorrentes."
            }
            actions={canCreate ? [{ label: "Incluir conta", onClick: () => setCreating(true) }] : []}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label={s.title}>
                <thead>
                  <tr>
                    <th scope="col" style={{ width: 40 }}>
                      <span className="visually-hidden">Selecionar</span>
                    </th>
                    <th scope="col">Vencimento</th>
                    <th scope="col">{s.contact}</th>
                    <th scope="col">Histórico</th>
                    <th scope="col">Parcela</th>
                    <th scope="col" className="text-end">
                      Valor
                    </th>
                    <th scope="col" className="text-end">
                      Saldo
                    </th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => {
                    const b = titleBadge(t);
                    return (
                      <tr key={t.id}>
                        <td>
                          {settleable(t) && (
                            <input
                              type="checkbox"
                              className="form-check-input"
                              aria-label={`Selecionar ${t.contactName} de ${formatDateBR(t.dueDate)}`}
                              checked={selected.has(t.id)}
                              onChange={() => toggle(t)}
                            />
                          )}
                        </td>
                        <td>
                          <Link to={s.detail} params={{ id: t.id }}>
                            {formatDateBR(t.dueDate)}
                          </Link>
                        </td>
                        <td>{t.contactName}</td>
                        <td>{t.description ?? t.originLabel ?? "—"}</td>
                        <td>{installmentLabel(t) ?? "—"}</td>
                        <td className="text-end">{formatBRL(t.amountCents)}</td>
                        <td className="text-end">{formatBRL(t.openCents)}</td>
                        <td>
                          <StatusBadge tone={b.tone}>{b.label}</StatusBadge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3">
              {summary && summary.count >= 0 && (
                <span className="text-muted small" data-testid="titles-summary">
                  {summary.count} {summary.count === 1 ? "conta" : "contas"}
                  {summary.totalCents !== null && ` · total ${formatBRL(summary.totalCents)}`}
                  {summary.openCents !== null && ` · em aberto ${formatBRL(summary.openCents)}`}
                </span>
              )}
              {list.hasNextPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              )}
            </div>
          </>
        )}
      </div>
      {creating && <TitleDialog side={side} onClose={() => setCreating(false)} onSaved={changed} />}
      {bulkOpen && (
        <BulkSettleDialog
          side={side}
          titles={picked}
          onClose={() => setBulkOpen(false)}
          onDone={(r) => {
            const byId = new Map(picked.map((t) => [t.id, `${t.contactName} (${formatDateBR(t.dueDate)})`]));
            const failed = r.results.filter((x) => !x.ok);
            setFailures(failed.map((x) => ({ label: byId.get(x.id) ?? x.id, message: x.message ?? "Erro" })));
            setSelected(
              new Map(picked.filter((t) => failed.some((f) => f.id === t.id)).map((t) => [t.id, t])),
            );
            toast(r.failCount ? "warning" : "success", bulkSettleSummary(r));
            changed();
            qc.invalidateQueries({ queryKey: ["fin-accounts", companyId] });
          }}
        />
      )}
    </>
  );
}

function BulkSettleDialog({
  side,
  titles,
  onClose,
  onDone,
}: {
  side: TitleSide;
  titles: TitleView[];
  onClose(): void;
  onDone(r: BulkSettleResult): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const options = useTitleOptions(side);
  const [open, setOpen] = useState(true);
  const [key] = useState(() => crypto.randomUUID());
  const [paidAt, setPaidAt] = useState(todayInSaoPaulo());
  const [accountId, setAccountId] = useState("");
  const s = SIDE[side];
  const run = useMutation({
    mutationFn: () =>
      api<BulkSettleResult>(`${s.api}/bulk-settle`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ paidAt, financialAccountId: accountId, ids: titles.map((t) => t.id) }),
      }),
    onSuccess: (r) => {
      onDone(r);
      setOpen(false);
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <Modal
      open={open}
      title={`${s.bulk} (${titles.length})`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!accountId || run.isPending}
            onClick={() => run.mutate()}
          >
            Confirmar
          </button>
        </>
      }
    >
      <p>
        Cada conta é baixada pelo saldo em aberto, com juros e multa calculados até a data informada. As que
        não puderem ser baixadas aparecem na lista de erros.
      </p>
      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label" htmlFor="bulk-paid-at">
            Data
          </label>
          <input
            id="bulk-paid-at"
            type="date"
            className="form-control"
            value={paidAt}
            max={todayInSaoPaulo()}
            onChange={(e) => setPaidAt(e.target.value)}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label" htmlFor="bulk-account">
            Conta
          </label>
          <select
            id="bulk-account"
            className="form-select"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">Selecione</option>
            {options.data?.accounts
              .filter((a) => a.active)
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
          </select>
        </div>
      </div>
    </Modal>
  );
}
